import { Button, Col, Form, Input, Row, Select, Typography } from "antd";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ACCOUNT_TYPES } from "commons/constants";
import RoleCardSelector from "./RoleCardSelector";

const { Title, Paragraph } = Typography;

function Step2Community({
  form,
  onNext,
  onBack,
  activityOptions,
  loadingActivities,
  cityOptions,
  getAreaOptions,
  loadingGeo,
}) {
  const cityCode = Form.useWatch("city_code");
  const areaOptions = cityCode ? getAreaOptions(cityCode) : [];

  return (
    <>
      <Title level={4} style={{ marginBottom: 4 }}>
        Community Basics
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Tell us about your community or brand.
      </Paragraph>

      <Form.Item
        name="role"
        label="What best describes you?"
        rules={[
          { required: true, message: "Please select a role to continue." },
        ]}
        style={{ marginBottom: 24 }}
      >
        <RoleCardSelector />
      </Form.Item>

      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.role !== curr.role}
          >
            {({ getFieldValue }) => {
              const isWorkshopOrganiser =
                getFieldValue("role") === ACCOUNT_TYPES.WORKSHOP_ORGANISER;
              return (
                <Form.Item
                  name="community_name"
                  label={isWorkshopOrganiser ? "Event Name" : "Community Name"}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: `${isWorkshopOrganiser ? "Event" : "Community"} name is required.`,
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      isWorkshopOrganiser
                        ? "e.g. FitFusion Events"
                        : "e.g. FitSquad Mumbai"
                    }
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="categories" label="Category">
            <Select
              mode="multiple"
              placeholder="Select or type your activity categories…"
              options={activityOptions}
              loading={loadingActivities}
              tokenSeparators={[","]}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="city_code"
            label="City"
            rules={[{ required: true, message: "City is required." }]}
          >
            <Select
              placeholder="Select city"
              options={cityOptions}
              loading={loadingGeo}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={() => form.setFieldValue("area_codes", [])}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="area_codes"
            label="Areas / Localities"
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: "Select at least one area.",
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder={
                cityCode ? "Select areas" : "Select a city first"
              }
              options={areaOptions}
              loading={loadingGeo}
              disabled={!cityCode}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="website"
            label="Website"
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Website is optional.",
              },
            ]}
          >
            <Input
              placeholder="e.g. https://www.fitfusion.com"
              type="url"
              maxLength={225}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="instagram_url" label="Instagram URL">
            <Input
              placeholder="e.g. https://www.instagram.com/fitfusion"
              type="url"
              maxLength={225}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Button icon={<ArrowLeft size={14} />} onClick={onBack}>
            Back
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={onNext}
            icon={<ArrowRight size={14} />}
            iconPosition="end"
          >
            Next: Access Model
          </Button>
        </Col>
      </Row>
    </>
  );
}

export default Step2Community;
